<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :key="tableKey"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :perPage="itemsPerPage"
            :enableMultiSelect="false"
            :enableDragDrop="false"
            @page-changed="handlePageChange"
            @per-page-changed="handlePerPageChange"
            @sort="handleSort"
        >
            <template #column-roles="{ item }">
                <div
                    class="ppv2-inline-flex ppv2-items-center ppv2-px-0 ppv2-py-0.5 ppv2-rounded-full ppv2-text-xs poppinsMedium poppinsMedium"
                >
                    {{ functionalRole(item) }}
                </div>
            </template>
            <template #column-status_id="{ item }">
                <div
                    class="ppv2-inline-flex ppv2-items-center ppv2-px-2.5 ppv2-py-0.5 ppv2-rounded-full ppv2-text-xs poppinsMedium poppinsMedium"
                    :style="getStatusStyle(item.status_id)"
                >
                    {{ getStatusLabel(item.status_id) }}
                </div>
            </template>
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <a
                            :href="`/back-office/v3/staffs/${item.id}/edit`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <FilePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.edit')
                            }}</span>
                        </a>
                        <!-- Désactiver/Réactiver -->
                        <div
                            v-if="item.status_id == 1"
                            @click="handleDisable(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <UserX :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.disable')
                            }}</span>
                        </div>
                        <div
                            v-else
                            @click="handleEnable(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <UserCheck :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.enable')
                            }}</span>
                        </div>

                        <!-- Supprimer -->
                        <div
                            @click="deleteSingleItem(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Trash2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.delete')
                            }}</span>
                        </div>
                    </div>
                </div>
            </template>
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>
                    </div>

                    <!-- Create Button Dropdown -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="navigateToCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('common.create') }}</span>
                    </BaseButton>
                </div>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import {
    Plus,
    Trash2,
    SquarePen,
    ChevronDown,
    MoreVertical,
    FilePen,
    UserCheck,
    UserX,
    KeyRound,
    Computer,
    CalendarPlus,
} from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import FilterByType from '../../Shared/FilterByType.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'StaffTable',
    props: {
        token: { type: String, default: '' },
    },
    components: {
        Plus,
        ChevronDown,
        SquarePen,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
        FilterByType,
        MoreVertical,
        FilePen,
        UserCheck,
        UserX,
        KeyRound,
        Computer,
        CalendarPlus,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('staff_items_per_page', 10),
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'first_name', label: this.$t('staff.table.columns.first_name'), sortable: true, type: 'text' },
                { key: 'last_name', label: this.$t('staff.table.columns.last_name'), sortable: true, type: 'text' },
                { key: 'email', label: this.$t('staff.table.columns.email'), sortable: true, type: 'text' },
                { key: 'roles', label: this.$t('staff.table.columns.roles'), sortable: true, type: 'text' },
                { key: 'status_id', label: this.$t('staff.table.columns.status'), sortable: true, type: 'text' },
                { key: 'actions', label: this.$t('staff.table.columns.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            showCreateDropdown: false,
            deletePopupTitle: this.$t('staff.table.delete_popup.title'),
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            items: {
                data: [],
                total: 0,
                last_page: 1,
            },
            tableKey: 0,
            orderBy: 'id',
            orderDirection: 'desc',
            activeActionMenu: null, // Track which action menu is open
        }
    },
    computed: {
        paginatedItems() {
            return this.items.data || []
        },
        totalPages() {
            return this.items.last_page || 1
        },
    },
    mounted() {
        this.fetchItems()
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        // A staff always carries `employee` (base marker) + one functional role; roles come back
        // ordered by id so roles[0] is usually `employee`. Show the functional role instead.
        functionalRole(item) {
            const roles = item.roles || []
            const fn = roles.find((r) => r.name !== 'employee') || roles[0]
            return fn ? fn.display_name || fn.name : ''
        },
        navigateToCreate() {
            window.location.href = '/back-office/v3/staffs/create'
        },
        toggleCreateDropdown() {
            this.showCreateDropdown = !this.showCreateDropdown
        },
        handleClickOutside(event) {
            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]
                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu

                if (!btnEl?.contains(event.target) && !menuEl?.contains(event.target)) {
                    this.activeActionMenu = null
                }
            }
        },
        formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('fr-FR')
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchItems()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('staff_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchItems()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchItems()
        },
        selectType(type) {
            this.selectedType = type
            this.currentPage = 1
            this.fetchItems()
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchItems()
        },
        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('staff.table.delete_popup.description_single')
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                if (this.itemToDelete) {
                    const response = await axios.delete(`/api/v3/staffs/${this.itemToDelete}`, {
                        headers: { Authorization: `Bearer ${this.token}` },
                    })
                    this.$toast.success(response?.data?.message || this.$t('staff.table.delete_popup.success'))
                    this.fetchItems()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
            this.showDeletePopup = false
            this.itemToDelete = null
        },
        openEditTab(item) {
            window.location.href = `/back-office/v3/staffs/${item.id}/edit`
        },
        async fetchItems() {
            this.isLoading = true
            this.error = null
            try {
                let url = `/api/v3/staffs?currentPage=${this.currentPage}&itemsPerPage=${this.itemsPerPage}&search=${this.searchQuery}&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}&roles=true`
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${this.token}` } })
                const data = response.data
                this.items = {
                    data: data.data || [],
                    total: data.total || 0,
                    last_page: data.last_page || 1,
                }
            } catch (err) {
                this.error = err.message || this.$t('common.error')
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },

        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 is min-width of dropdown
            }
        },

        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        getStatusLabel(statusId) {
            const labels = {
                1: this.$t('staff.statuses.active'),
                2: this.$t('staff.statuses.suspended'),
                3: this.$t('staff.statuses.forbidden'),
                4: this.$t('staff.statuses.deleting'),
                5: this.$t('staff.statuses.deleted'),
            }
            return labels[statusId] || statusId
        },
        getStatusStyle(statusId) {
            const styles = {
                1: { backgroundColor: '#E1FCEF', color: '#14804A' }, // Green
                2: { backgroundColor: '#FFF4E5', color: '#B35B00' }, // Orange
                3: { backgroundColor: '#FFEBEB', color: '#D12424' }, // Red
                4: { backgroundColor: '#FFF0F0', color: '#E03131' }, // Light Red
                5: { backgroundColor: '#F1F3F5', color: '#495057' }, // Gray
            }
            return styles[statusId] || { backgroundColor: '#F1F3F5', color: '#495057' }
        },

        handleDisable(id) {
            this.activeActionMenu = null
            this.disable(id)
        },

        async disable(id) {
            this.isLoading = true
            try {
                const res = await axios.post(
                    `/api/v3/staffs/disable`,
                    { id: id },
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                if (res?.data?.success) {
                    this.fetchItems()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error disable:', error)
            } finally {
                this.isLoading = false
            }
        },

        handleEnable(id) {
            this.activeActionMenu = null
            this.enable(id)
        },

        async enable(id) {
            this.isLoading = true
            try {
                const res = await axios.post(
                    `/api/v3/staffs/enable`,
                    { id: id },
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                if (res?.data?.success) {
                    this.fetchItems()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error enable:', error)
            } finally {
                this.isLoading = false
            }
        },
    },
    watch: {},
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.bgBanner {
    background-color: rgba(0, 117, 255, var(--tw-bg-opacity));
}

.bgPopup {
    background-color: rgba(219, 168, 0, var(--tw-bg-opacity));
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}

.menuDropdown {
    background: white;
    color: #94a3b8;
    margin-top: 10px;
    z-index: 50;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdownItem:hover {
    background-color: #1a1f37;
    color: white !important;
}

.dropdownItem:hover * {
    color: white !important;
}

.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}

.dark {
    .menuDropdown {
        background-color: #2c2c2ce5;
    }

    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }

    .actionItem:hover * {
        color: white !important;
    }
}

.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
